import { BlockReasonId, BlockReasonOption, ChatBlockFlags, ChatContact, MessageBlockedPayload } from '../types/chat.types';
import { normalizeContactIdForQueue } from './cross-view-hub-message-queue';

const DEFAULT_BLOCKED_SEND_MESSAGE =
  'You cannot send messages because this conversation is blocked.';

export const DEFAULT_CHAT_BLOCK_FLAGS: ChatBlockFlags = {
  isBlockedByMe: false,
  hasBlockedMe: false,
  canSendMessage: true,
};

export const CHAT_BLOCK_STATUS_CHANGED_EVENT = 'chatBlockStatusChanged';
export const CHAT_BLOCK_STATUS_STORAGE_KEY = 'virtcoChatBlockStatusChanged';

export function dispatchChatBlockStatusChanged(
  recipientId: string,
  flags: Partial<ChatBlockFlags>
): void {
  if (typeof window === 'undefined' || !recipientId) {
    return;
  }
  const detail = { recipientId, flags, at: Date.now() };
  window.dispatchEvent(new CustomEvent(CHAT_BLOCK_STATUS_CHANGED_EVENT, { detail }));
  try {
    localStorage.setItem(CHAT_BLOCK_STATUS_STORAGE_KEY, JSON.stringify(detail));
  } catch {
    // Ignore quota / private mode — in-memory event still notifies the same tab.
  }
}

/** API-backed titles; used only if GET BlockReasons fails. */
export const FALLBACK_BLOCK_REASONS: BlockReasonOption[] = [
  {
    id: BlockReasonId.Spam,
    code: 'SPAM',
    title: 'Spam',
    description: 'Unsolicited or repetitive messages.',
    displayOrder: 1,
    requiresComment: false,
  },
  {
    id: BlockReasonId.Harassment,
    code: 'HARASSMENT',
    title: 'Harassment or bullying',
    description: 'Threatening, bullying, or harassing messages.',
    displayOrder: 2,
    requiresComment: false,
  },
  {
    id: BlockReasonId.InappropriateContent,
    code: 'INAPPROPRIATE_CONTENT',
    title: 'Inappropriate content',
    description: 'Offensive or inappropriate messages or media.',
    displayOrder: 3,
    requiresComment: false,
  },
  {
    id: BlockReasonId.FakeProfile,
    code: 'FAKE_PROFILE',
    title: 'Fake profile or impersonation',
    description: 'This account appears fake or is impersonating someone.',
    displayOrder: 4,
    requiresComment: false,
  },
  {
    id: BlockReasonId.ScamOrFraud,
    code: 'SCAM_OR_FRAUD',
    title: 'Scam or fraud',
    description: 'Requests for money or other fraudulent activity.',
    displayOrder: 5,
    requiresComment: false,
  },
  {
    id: BlockReasonId.HateSpeech,
    code: 'HATE_SPEECH',
    title: 'Hate speech',
    description: 'Hateful or discriminatory language.',
    displayOrder: 6,
    requiresComment: false,
  },
  {
    id: BlockReasonId.Other,
    code: 'OTHER',
    title: 'Other',
    description: 'Another reason not listed. Please add details.',
    displayOrder: 7,
    requiresComment: true,
  },
];

const readRecord = (source: unknown): Record<string, unknown> | null => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return null;
  }
  return source as Record<string, unknown>;
};

const readBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 1 || value === '1' || value === 'true' || value === 'True') {
    return true;
  }
  if (value === 0 || value === '0' || value === 'false' || value === 'False') {
    return false;
  }
  return fallback;
};

export const sourceHasChatBlockFields = (source: unknown): boolean => {
  const row = readRecord(source);
  if (!row) {
    return false;
  }
  return (
    'isBlockedByMe' in row ||
    'IsBlockedByMe' in row ||
    'hasBlockedMe' in row ||
    'HasBlockedMe' in row ||
    'canSendMessage' in row ||
    'CanSendMessage' in row
  );
};

export const parseBlockFlags = (source: unknown): ChatBlockFlags => {
  const row = readRecord(source);
  if (!row) {
    return {
      isBlockedByMe: false,
      hasBlockedMe: false,
      canSendMessage: true,
    };
  }

  const isBlockedByMe = readBoolean(row.isBlockedByMe ?? row.IsBlockedByMe, false);
  const hasBlockedMe = readBoolean(row.hasBlockedMe ?? row.HasBlockedMe, false);
  const canSendRaw = row.canSendMessage ?? row.CanSendMessage;
  const canSendMessage =
    canSendRaw === undefined || canSendRaw === null
      ? !(isBlockedByMe || hasBlockedMe)
      : readBoolean(canSendRaw, !(isBlockedByMe || hasBlockedMe));

  return { isBlockedByMe, hasBlockedMe, canSendMessage };
};

export const applyBlockFlagsToContact = (
  contact: ChatContact,
  flags: Partial<ChatBlockFlags>
): ChatContact => {
  const isBlockedByMe = flags.isBlockedByMe ?? contact.isBlockedByMe ?? false;
  const hasBlockedMe = flags.hasBlockedMe ?? contact.hasBlockedMe ?? false;
  const canSendMessage =
    flags.canSendMessage !== undefined
      ? flags.canSendMessage
      : !(isBlockedByMe || hasBlockedMe);

  if (
    contact.isBlockedByMe === isBlockedByMe &&
    contact.hasBlockedMe === hasBlockedMe &&
    contact.canSendMessage === canSendMessage
  ) {
    return contact;
  }

  return {
    ...contact,
    isBlockedByMe,
    hasBlockedMe,
    canSendMessage,
  };
};

export const isDirectChat = (contact: ChatContact | null | undefined): boolean => {
  return Boolean(contact?.id) && !contact?.isAGroup;
};

export const canShowBlockAction = (
  contact: ChatContact | null | undefined,
  currentUserId?: string | null
): boolean => {
  if (!isDirectChat(contact) || !contact) {
    return false;
  }
  if (contact.isBlockedByMe) {
    return false;
  }
  const selfId = normalizeContactIdForQueue(currentUserId);
  const peerId = normalizeContactIdForQueue(contact.id);
  if (selfId && peerId && selfId === peerId) {
    return false;
  }
  return true;
};

export const canShowUnblockAction = (contact: ChatContact | null | undefined): boolean => {
  return isDirectChat(contact) && Boolean(contact?.isBlockedByMe);
};

export const getBlockBannerTextFromFlags = (
  flags: { canSendMessage?: boolean; isBlockedByMe?: boolean } | null | undefined
): string => {
  if (!flags || flags.canSendMessage !== false) {
    return '';
  }
  if (flags.isBlockedByMe) {
    return 'You have blocked this person';
  }
  return 'You cannot message this user.';
};

export const getBlockBannerText = (contact: ChatContact | null | undefined): string => {
  return getBlockBannerTextFromFlags(contact);
};

export const parseBlockReasons = (source: unknown): BlockReasonOption[] => {
  if (!Array.isArray(source)) {
    return [];
  }

  const mapped = source
    .map((item): BlockReasonOption | null => {
      const row = readRecord(item);
      if (!row) {
        return null;
      }
      const id = Number(row.id ?? row.Id);
      if (!Number.isFinite(id) || id <= 0) {
        return null;
      }
      const title = String(row.title ?? row.Title ?? '').trim();
      if (!title) {
        return null;
      }
      return {
        id,
        code: String(row.code ?? row.Code ?? '').trim(),
        title,
        description: String(row.description ?? row.Description ?? '').trim(),
        displayOrder: Number(row.displayOrder ?? row.DisplayOrder ?? id) || id,
        requiresComment: readBoolean(row.requiresComment ?? row.RequiresComment, id === BlockReasonId.Other),
      };
    })
    .filter((item): item is BlockReasonOption => Boolean(item));

  return mapped.sort((a, b) => a.displayOrder - b.displayOrder);
};

export const parseMessageBlockedPayload = (source: unknown): MessageBlockedPayload | null => {
  const row = readRecord(source);
  if (!row) {
    return null;
  }

  const recipientId = String(row.recipientId ?? row.RecipientId ?? '').trim();
  if (!recipientId) {
    return null;
  }

  const canSendRaw = row.canSendMessage ?? row.CanSendMessage;
  const message = String(row.message ?? row.Message ?? DEFAULT_BLOCKED_SEND_MESSAGE).trim();

  return {
    recipientId,
    canSendMessage: canSendRaw === undefined || canSendRaw === null ? false : readBoolean(canSendRaw, false),
    message: message || DEFAULT_BLOCKED_SEND_MESSAGE,
  };
};

export const BLOCKED_SEND_MESSAGE = DEFAULT_BLOCKED_SEND_MESSAGE;
